import "./styles.css";
import { animate, stagger } from "motion";
import type { Repo } from "./data/repos";
import { repoData } from "./data/repos";

const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function actionLink(label: string, url: string, icon: string): HTMLAnchorElement {
  const link = document.createElement("a");
  link.href = url;
  link.className = "card-link";
  link.innerHTML = `<span class="material-symbols-rounded" aria-hidden="true">${icon}</span>${label}`;
  if (url.startsWith("http")) {
    link.target = "_blank";
    link.rel = "noreferrer";
  }
  return link;
}

function makeExternal(link: HTMLAnchorElement, url: string): void {
  if (url.startsWith("http")) {
    link.target = "_blank";
    link.rel = "noreferrer";
  }
}

function createRepoCard(repo: Repo): HTMLElement {
  const card = document.createElement("article");
  card.className = `repo-card${repo.featured ? " repo-card-featured" : ""}`;
  card.setAttribute("data-repo", repo.name);
  card.style.setProperty("--accent-color", repo.accent);

  if (repo.image) {
    const imageLink = document.createElement("a");
    imageLink.className = "card-image-link";
    imageLink.href = repo.url;
    imageLink.setAttribute("aria-label", `Explore ${repo.name}`);
    makeExternal(imageLink, repo.url);
    imageLink.innerHTML = `<img src="${repo.image}" alt="${repo.imageAlt ?? `${repo.name} running in VSCodium`}" class="card-image" loading="lazy" />`;
    card.appendChild(imageLink);
  }

  const content = document.createElement("div");
  content.className = "repo-card-content";

  const top = document.createElement("div");
  top.className = "repo-top";
  const icon = repo.icon
    ? `<img src="${repo.icon}" alt="" class="repo-icon-img" />`
    : `<span class="material-symbols-rounded" aria-hidden="true">folder_special</span>`;
  top.innerHTML = `<span class="repo-badge">${icon}${repo.status ?? "Project"}</span>`;

  const title = document.createElement("h3");
  title.className = "repo-title";
  const titleLink = document.createElement("a");
  titleLink.href = repo.url;
  titleLink.textContent = repo.name;
  makeExternal(titleLink, repo.url);
  title.appendChild(titleLink);

  const description = document.createElement("p");
  description.className = "repo-desc";
  description.textContent = repo.description;

  const pills = document.createElement("div");
  pills.className = "pill-row";
  [...repo.tags, repo.language].forEach((tag, index) => {
    const pill = document.createElement("span");
    pill.className = index === repo.tags.length ? "pill pill-accent" : "pill";
    pill.textContent = tag;
    pills.appendChild(pill);
  });

  const actions = document.createElement("div");
  actions.className = "card-actions";
  actions.appendChild(actionLink(repo.actionLabel ?? (repo.featured ? "View details" : "Open project"), repo.url, repo.featured ? "arrow_forward" : "open_in_new"));
  repo.links?.forEach((link) => actions.appendChild(actionLink(link.label, link.url, link.icon)));

  content.append(top, title, description, pills, actions);
  card.appendChild(content);
  return card;
}

function renderRepos(): void {
  const viewerGrid = document.querySelector<HTMLDivElement>("#viewer-grid");
  const projectGrid = document.querySelector<HTMLDivElement>("#project-grid");
  if (!viewerGrid || !projectGrid) return;

  repoData.forEach((repo) => {
    (repo.featured ? viewerGrid : projectGrid).appendChild(createRepoCard(repo));
  });
}

function animatePage(): void {
  if (prefersReducedMotion) return;
  animate(".hero-reveal", { opacity: [0, 1], y: [16, 0] }, { duration: 0.75, delay: stagger(0.07), easing: "easeOut" });
  animate(".repo-card", { opacity: [0, 1], y: [20, 0] }, { duration: 0.65, delay: stagger(0.08, { start: 0.25 }), easing: "easeOut" });
}

renderRepos();
animatePage();
